'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useUserWalletPortfolioSummary } from '@/hooks/positions/user-portfolio/selectors';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { useBalanceDisplayStore } from '@/stores/balance-display';

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

/**
 * - `combined`: single number (USDC + holdings when toggle is ON, USDC only when OFF).
 *   Use where space is tight or breakdown context is already visible.
 * - `split`: primary combined number with a muted "Cash: $X · Holdings: $Y" line beneath.
 *   Used in places like portfolio per-wallet rows where the breakdown adds clarity.
 * - `compact-split`: minimal split — primary combined number, secondary labelled
 *   `Cash: $X • Holdings: $Y` in caption text. Designed for tight rows like the
 *   manage-wallets modal.
 */
export type WalletBalanceVariant = 'combined' | 'split' | 'compact-split';
interface WalletBalanceDisplayProps {
  /** Wallet address (Solana base58 pubkey). Never the wallet ID. */
  address: string;
  variant: WalletBalanceVariant;
  /** Optional className applied to the outer wrapper. */
  className?: string;
  /** Optional className applied to the primary (combined) number element. */
  primaryClassName?: string;
  /** Optional className applied to the breakdown line (split variants only). */
  secondaryClassName?: string;
  /** Visual alignment for the wrapper (defaults to right). */
  align?: 'left' | 'right';
  /**
   * Force the include-holdings behaviour regardless of the persisted store
   * value. Used by surfaces (e.g. wallet dropdown) that always want to show
   * the combined cash + holdings breakdown without exposing a toggle.
   */
  forceIncludeHoldings?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single source of truth for rendering a wallet's balance with optional
 * cash / holdings breakdown. Consumers pick a variant; the component handles
 * the include-holdings toggle, wallet-scoped portfolio selector, and loading/error
 * states.
 *
 * The combined recipe is always:
 *   indexed cash D6 + (toggle ? consolidated holdings display mark D6 : 0)
 *
 * Required pool state is explicit: when holdings are enabled but cannot be
 * quoted, the combined value is unavailable instead of falling back to cash.
 */
export function WalletBalanceDisplay(t0) {
  const $ = _c(45);
  const {
    address,
    variant,
    className,
    primaryClassName,
    secondaryClassName,
    align: t1,
    forceIncludeHoldings
  } = t0;
  const align = t1 === undefined ? "right" : t1;
  const t = useTranslations("profile.stats");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    user
  } = useAuth();
  const t2 = user?.id ?? "";
  let t3;
  if ($[0] !== address || $[1] !== t2) {
    t3 = {
      userId: t2,
      wallet: address
    };
    $[0] = address;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const summary = useUserWalletPortfolioSummary(t3);
  const includeHoldingsFromStore = useBalanceDisplayStore(_temp);
  const includeHoldings = forceIncludeHoldings ?? includeHoldingsFromStore;
  const showHoldingsLine = includeHoldings && variant !== "combined";
  const cashD6 = summary.isError ? null : summary.cashD6;
  const holdingsD6 = summary.isError ? null : summary.holdingsD6;
  const combinedD6 = summary.isPending || summary.isError ? null : includeHoldings ? summary.totalD6 : summary.cashD6;
  let t4;
  if ($[3] !== combinedD6 || $[4] !== formatD6) {
    t4 = formatD6((combinedD6 ?? BigInt(0)).toString());
    $[3] = combinedD6;
    $[4] = formatD6;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const combinedDisplay = t4;
  let t5;
  let t6;
  if ($[6] !== align || $[7] !== cashD6 || $[8] !== className || $[9] !== combinedDisplay || $[10] !== formatD6 || $[11] !== holdingsD6 || $[12] !== primaryClassName || $[13] !== secondaryClassName || $[14] !== showHoldingsLine || $[15] !== t || $[16] !== variant) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const cashDisplay = formatD6((cashD6 ?? BigInt(0)).toString());
      const holdingsDisplay = formatD6((holdingsD6 ?? BigInt(0)).toString());
      const wrapperAlignClass = align === "right" ? "items-end text-right" : "items-start text-left";
      if (variant === "combined") {
        let t7;
        if ($[19] !== className || $[20] !== primaryClassName) {
          t7 = cn("tabular-nums", primaryClassName, className);
          $[19] = className;
          $[20] = primaryClassName;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        let t8;
        if ($[22] !== combinedDisplay || $[23] !== t7) {
          t8 = <span className={t7} data-testid="wallet-balance-display">{combinedDisplay}</span>;
          $[22] = combinedDisplay;
          $[23] = t7;
          $[24] = t8;
        } else {
          t8 = $[24];
        }
        t6 = t8;
        break bb0;
      }
      if (variant === "compact-split") {
        let t7;
        if ($[25] !== className || $[26] !== wrapperAlignClass) {
          t7 = cn("flex flex-col leading-tight", wrapperAlignClass, className);
          $[25] = className;
          $[26] = wrapperAlignClass;
          $[27] = t7;
        } else {
          t7 = $[27];
        }
        let t8;
        if ($[28] !== primaryClassName) {
          t8 = cn("whitespace-nowrap text-body font-medium tabular-nums", primaryClassName);
          $[28] = primaryClassName;
          $[29] = t8;
        } else {
          t8 = $[29];
        }
        let t9;
        if ($[30] !== combinedDisplay || $[31] !== t8) {
          t9 = <span className={t8}>{combinedDisplay}</span>;
          $[30] = combinedDisplay;
          $[31] = t8;
          $[32] = t9;
        } else {
          t9 = $[32];
        }
        const t10 = showHoldingsLine ? <span className={cn("flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-caption text-foreground-secondary", align === "right" ? "justify-end" : "justify-start", secondaryClassName)}><span className="min-w-0 whitespace-nowrap tabular-nums">{t("cash_with_amount", {
              amount: cashDisplay
            })}</span><span aria-hidden="true">•</span><span className="min-w-0 whitespace-nowrap tabular-nums">{t("holdings_with_amount", {
              amount: holdingsDisplay
            })}</span></span> : null;
        let t11;
        if ($[33] !== t10 || $[34] !== t7 || $[35] !== t9) {
          t11 = <div className={t7} data-testid="wallet-balance-display">{t9}{t10}</div>;
          $[33] = t10;
          $[34] = t7;
          $[35] = t9;
          $[36] = t11;
        } else {
          t11 = $[36];
        }
        t6 = t11;
        break bb0;
      }
      let t7;
      if ($[37] !== className || $[38] !== wrapperAlignClass) {
        t7 = cn("flex flex-col leading-tight", wrapperAlignClass, className);
        $[37] = className;
        $[38] = wrapperAlignClass;
        $[39] = t7;
      } else {
        t7 = $[39];
      }
      let t8;
      if ($[40] !== primaryClassName) {
        t8 = cn("text-body font-medium tabular-nums", primaryClassName);
        $[40] = primaryClassName;
        $[41] = t8;
      } else {
        t8 = $[41];
      }
      let t9;
      if ($[42] !== combinedDisplay || $[43] !== t8) {
        t9 = <span className={t8}>{combinedDisplay}</span>;
        $[42] = combinedDisplay;
        $[43] = t8;
        $[44] = t9;
      } else {
        t9 = $[44];
      }
      t5 = <div className={t7} data-testid="wallet-balance-display">{t9}{showHoldingsLine ? <span className={cn("flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-caption text-foreground-secondary", align === "right" ? "justify-end" : "justify-start", secondaryClassName)}><span className="tabular-nums">{t("cash_with_amount", {
              amount: cashDisplay
            })}</span><span aria-hidden="true">·</span><span className="tabular-nums">{t("holdings_with_amount", {
              amount: holdingsDisplay
            })}</span></span> : null}</div>;
    }
    $[6] = align;
    $[7] = cashD6;
    $[8] = className;
    $[9] = combinedDisplay;
    $[10] = formatD6;
    $[11] = holdingsD6;
    $[12] = primaryClassName;
    $[13] = secondaryClassName;
    $[14] = showHoldingsLine;
    $[15] = t;
    $[16] = variant;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  return t5;
}
function _temp(s) {
  return s.includeHoldings;
}